import http from '../http'

/**
 * 获取当前登录用户的权限信息
 * @returns {Promise} 返回当前用户的权限信息，包括权限码列表、权限树等
 * @description 用于前端权限控制，获取当前登录用户的所有权限信息
 */
export function get_current_user_permissions() {
  return http.get('/permissions/user/permissions')
}

/**
 * 获取指定用户的权限信息
 * @param {Number|String} userId - 用户ID
 * @returns {Promise} 返回指定用户的权限信息
 */
export function get_user_permissions(userId) {
  return http.get(`/permissions/user/${userId}/permissions`)
}

/**
 * 分页查询权限列表
 * @param {Object} params - 查询参数
 * @param {Number} [params.page] - 页码（从0开始），默认0
 * @param {Number} [params.size] - 每页数量，默认10
 * @param {String} [params.permissionName] - 权限名称（模糊搜索）
 * @param {String} [params.permissionType] - 权限类型（MENU/BUTTON/API，精确匹配）
 * @returns {Promise} 返回权限信息列表和分页信息
 */
export function query_permissions_pagination(params = {}) {
  const queryParams = {
    page: params.page !== undefined ? params.page : 0,
    size: params.size || 10
  }
  if (params.permissionName) {
    queryParams.permissionName = params.permissionName
  }
  if (params.permissionType) {
    queryParams.permissionType = params.permissionType
  }

  return http.get('/permissions', { params: queryParams })
}

/**
 * 根据ID查询权限
 * @param {Number|String} id - 权限ID
 * @returns {Promise} 返回权限详细信息
 */
export function get_permission_by_id(id) {
  return http.get(`/permissions/${id}`)
}

/**
 * 根据权限码查询权限
 * @param {String} permissionCode - 权限码
 * @returns {Promise} 返回权限详细信息
 */
export function get_permission_by_code(permissionCode) {
  return http.get(`/permissions/code/${permissionCode}`)
}

/**
 * 根据类型查询权限列表
 * @param {String} permissionType - 权限类型（MENU/BUTTON/API）
 * @returns {Promise} 返回该类型的权限列表
 */
export function get_permissions_by_type(permissionType) {
  return http.get(`/permissions/type/${permissionType}`)
}

/**
 * 获取权限树
 * @returns {Promise} 返回完整的权限树结构
 */
export function get_permission_tree() {
  return http.get('/permissions/tree')
}

/**
 * 获取角色的权限树
 * @param {Number|String} roleId - 角色ID
 * @returns {Promise} 返回该角色的权限树结构
 */
export function get_role_permission_tree(roleId) {
  return http.get(`/permissions/role/${roleId}/tree`)
}

/**
 * 创建权限
 * @param {Object} data - 权限数据
 * @param {String} data.permissionCode - 权限码（唯一标识）
 * @param {String} data.permissionName - 权限名称
 * @param {String} data.permissionType - 权限类型（MENU/BUTTON/API）
 * @param {String} [data.resourcePath] - 资源路径
 * @param {Number} [data.parentId] - 父权限ID
 * @param {Number} [data.sortOrder] - 排序顺序
 * @param {String} [data.description] - 权限描述
 * @returns {Promise} 返回创建的权限信息
 */
export function create_permission(data) {
  return http.post('/permissions', data)
}

/**
 * 更新权限
 * @param {Number|String} id - 权限ID
 * @param {Object} data - 权限数据（所有字段可选）
 * @returns {Promise} 返回更新后的权限信息
 */
export function update_permission(id, data) {
  return http.put(`/permissions/${id}`, data)
}

/**
 * 删除权限
 * @param {Number|String} id - 权限ID
 * @returns {Promise} 返回删除结果
 * @description 执行软删除，如果权限下有子权限或已被角色使用，无法删除
 */
export function delete_permission(id) {
  return http.delete(`/permissions/${id}`)
}

/**
 * 为角色分配权限
 * @param {Object} data - 分配数据
 * @param {Number} data.roleId - 角色ID
 * @param {Array<Number>} data.permissionIds - 权限ID列表
 * @returns {Promise} 返回分配结果
 * @description 此操作会先删除该角色的所有现有权限关联，然后创建新的关联
 */
export function assign_permissions_to_role(data) {
  return http.post('/permissions/assign', data)
}

export default {
  get_current_user_permissions,
  get_user_permissions,
  query_permissions_pagination,
  get_permission_by_id,
  get_permission_by_code,
  get_permissions_by_type,
  get_permission_tree,
  get_role_permission_tree,
  create_permission,
  update_permission,
  delete_permission,
  assign_permissions_to_role
}
